import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImageIcon from '@mui/icons-material/Image';
import type { IUseLiveCameraModal } from '../hooks';
import type { ILiveCameraModalProps } from '../interfaces';
import { useLiveCameraModal } from '../hooks';

interface ICameraViewProps {
  modal: IUseLiveCameraModal;
}

export const LiveCameraModalCameraView = ({ modal }: ICameraViewProps) => {
  if (modal.cameraError) {
    return (
      <Alert
        severity="warning"
        sx={{ width: '100%', maxWidth: 480 }}
        action={
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => void modal.startCamera()}>
              Retry Camera
            </Button>
            <Button size="small" variant="contained" onClick={() => modal.switchTab('upload')}>
              Upload Photo
            </Button>
          </Stack>
        }
      >
        {modal.cameraError}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 480,
        aspectRatio: '4 / 3',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'grey.900',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Box
        component="video"
        ref={modal.videoRef}
        autoPlay
        playsInline={modal.isIosInline}
        muted
        sx={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            width: { xs: 200, sm: 220 },
            height: { xs: 260, sm: 280 },
            borderRadius: '50%',
            border: '2px dashed rgba(255,255,255,0.7)',
          }}
        />
      </Box>
      <Chip
        label="Center your face inside the oval"
        size="small"
        sx={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: 'rgba(0,0,0,0.5)',
          color: '#fff',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export const LiveCameraModalUploadView = ({ modal }: ICameraViewProps) => (
  <Box sx={{ width: '100%', maxWidth: 480 }}>
    <input
      ref={modal.fileInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      hidden
      onChange={(e) => modal.handleInputChange(e.target.files?.[0])}
    />
    <Box
      role="button"
      tabIndex={0}
      onDragOver={modal.handleDragOver}
      onDragLeave={modal.handleDragLeave}
      onDrop={modal.handleDrop}
      onClick={() => modal.fileInputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') modal.fileInputRef.current?.click();
      }}
      sx={{
        width: '100%',
        minHeight: { xs: 220, sm: 280 },
        borderRadius: 3,
        border: '2px dashed',
        borderColor: modal.dragOver ? 'primary.main' : 'divider',
        bgcolor: modal.dragOver ? 'primary.50' : 'grey.100',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        cursor: 'pointer',
        p: 3,
        transition: 'all 200ms ease',
        '&:hover': { borderColor: 'primary.main' },
        '&:focus-visible': { outline: 2, outlineColor: 'primary.main' },
      }}
    >
      <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', width: 48, height: 48 }}>
        <ImageIcon />
      </Avatar>
      <Typography variant="body1" align="center">
        Drag and drop your photo here
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        or <Typography component="span" variant="body2" color="primary" fontWeight={600}>browse files</Typography> from your computer
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Supports JPG, PNG or WEBP (Max 10MB)
      </Typography>
    </Box>
  </Box>
);

export const LiveCameraModalPreview = ({ modal }: ICameraViewProps) => {
  if (!modal.capturedImage) return null;
  return (
    <Grid container spacing={2.5} alignItems="center">
      <Grid item xs={12} sm={6}>
        <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
          SELECTED PHOTO
        </Typography>
        <Box
          component="img"
          src={modal.capturedImage}
          alt="Preview"
          sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 2, border: 1, borderColor: 'divider' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, bgcolor: 'grey.100', p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={700} alignSelf="flex-start">
            PROFILE PREVIEW
          </Typography>
          <Avatar src={modal.capturedImage} alt="Avatar Preview" sx={{ width: 96, height: 96, border: '4px solid #fff', boxShadow: 2 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2">{modal.userName}</Typography>
            <Typography variant="caption" color="text.secondary">
              This is how your photo will appear
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

/**
 * Profile picture capture dialog — MUI `Dialog` (same overlay pattern as the
 * `/mui-preview` lab: 20px radius, pill `Tabs`, contained primary CTA).
 * Full-screen on xs, centered modal on sm+. Camera logic lives in
 * `useLiveCameraModal`; this file is presentation only.
 */
export const LiveCameraModal = (props: ILiveCameraModalProps) => {
  const modal = useLiveCameraModal(props);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={Boolean(modal.isOpen)}
      onClose={modal.handleCloseModal}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="live-camera-title"
    >
      {modal.isFlashActive && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 50, bgcolor: '#fff', opacity: 0.8, pointerEvents: 'none' }} />
      )}

      <DialogTitle id="live-camera-title" sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h3">Update Profile Picture</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Take a photo with your camera or upload an image
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={modal.handleCloseModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      {!modal.capturedImage && (
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1 }}>
          <Tabs
            value={modal.activeTab}
            onChange={(_e, value: 'camera' | 'upload') => modal.switchTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Picture source"
          >
            <Tab value="camera" label="Camera" icon={<PhotoCameraIcon fontSize="small" />} iconPosition="start" sx={{ minHeight: 44 }} />
            <Tab value="upload" label="Upload Photo" icon={<CloudUploadIcon fontSize="small" />} iconPosition="start" sx={{ minHeight: 44 }} />
          </Tabs>
        </Box>
      )}

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 340, py: 3 }}>
        {modal.activeTab === 'camera' && !modal.capturedImage && (
          <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <LiveCameraModalCameraView modal={modal} />
            {!modal.cameraError && (
              <Stack spacing={0.5} alignItems="center">
                <IconButton
                  aria-label="Take Photo"
                  onClick={modal.handleCapturePhoto}
                  disabled={modal.isCapturing}
                  sx={{ width: 64, height: 64, border: 2, borderColor: 'divider', bgcolor: 'background.paper' }}
                >
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                    <PhotoCameraIcon sx={{ color: 'primary.contrastText' }} />
                  </Avatar>
                </IconButton>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Take Photo
                </Typography>
              </Stack>
            )}
          </Stack>
        )}

        {modal.activeTab === 'upload' && !modal.capturedImage && (
          <LiveCameraModalUploadView modal={modal} />
        )}

        {modal.capturedImage && <LiveCameraModalPreview modal={modal} />}
      </DialogContent>

      <DialogActions sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1 }}>
        {modal.capturedImage ? (
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={modal.handleRetake}
              disabled={modal.isSaving}
            >
              Retake
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="text" onClick={modal.handleCloseModal} disabled={modal.isSaving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => void modal.handleSavePicture()} disabled={modal.isSaving}>
              {modal.isSaving ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              {modal.isSaving ? 'Saving...' : 'Save Picture'}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {modal.activeTab === 'camera'
                ? 'Click the camera button to take your photo'
                : 'Select an image file from your computer'}
            </Typography>
            <Button variant="outlined" onClick={modal.handleCloseModal}>
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
